import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Flame } from "lucide-react";
import { COPY, META_PIXEL_ID, PRESENTER_IMAGE_URL, PRIVACY_URL, TERMS_URL } from "@/config";
import { openWidgetFloater, WidgetEmbed } from "@/components/WidgetEmbed";

// Loads the Meta Pixel only when META_PIXEL_ID is set in config.js.
// Fires PageView here; Lead fires on the thank-you page.
const useMetaPixel = () => {
  useEffect(() => {
    if (!META_PIXEL_ID || window.fbq) return;
    const n = (window.fbq = function fbq() {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  }, []);
};

// Splits `text` on the configured phrases and wraps matches:
// highlights get the yellow marker, underlines get the thick indigo underline.
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const EmphasizedText = ({ text, highlights = [], underlines = [] }) => {
  const phrases = [...highlights, ...underlines].filter(Boolean);
  if (!phrases.length) return <>{text}</>;
  const re = new RegExp(`(${phrases.map(escapeRegExp).join("|")})`, "g");
  return (
    <>
      {text.split(re).map((part, i) => {
        if (highlights.includes(part) || underlines.includes(part))
          return (
            <span key={i} className="emphasis-phrase">
              {part}
            </span>
          );
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

// Pulsing dot used by the LIVE badge, "session starting" line and CTA buttons.
export const LiveDot = ({ dotClass = "bg-[var(--accent)]" }) => (
  <span className="relative flex h-2 w-2 shrink-0">
    <span
      className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${dotClass}`}
    />
    <span className={`relative inline-flex h-2 w-2 rounded-full ${dotClass}`} />
  </span>
);

const SectionHeading = ({ children, testId }) => (
  <h2
    data-testid={testId}
    className="text-3xl font-bold tracking-tight text-[var(--text)] sm:text-4xl"
  >
    {children}
  </h2>
);

export default function App() {
  useMetaPixel();

  const widgetAnchorRef = useRef(null);
  const flashTimeoutRef = useRef(null);
  const [flash, setFlash] = useState(false);
  // "unknown" | "inline" | "floater" — set by WidgetEmbed once the provider's
  // script reveals how this widget is configured.
  const [widgetMode, setWidgetMode] = useState("unknown");

  const scrollToWidget = useCallback(() => {
    // The floater trigger only exists when the provider configured this
    // widget as a popup — open it directly instead of scrolling.
    if (document.querySelector(".wf_floater_handler")) {
      openWidgetFloater();
      return;
    }
    widgetAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlash(true);
    window.clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = window.setTimeout(() => setFlash(false), 2000);
  }, []);

  useEffect(() => () => window.clearTimeout(flashTimeoutRef.current), []);

  return (
    <div className="min-h-screen bg-[var(--bg)] font-sans text-[var(--text)] antialiased">
      {/* ============ NAV ============ */}
      <nav
        data-testid="nav"
        className="sticky top-0 z-30 border-b border-[var(--border)] bg-[var(--bg)]/90 backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <span data-testid="nav-logo" className="flex items-center gap-2.5">
            <img src="/assets/icon-mark.png" alt="" className="h-8 w-8 rounded-full" />
            <span className="text-lg font-bold tracking-tight text-[var(--text)]">
              Appointext.ai
            </span>
          </span>
          <span
            data-testid="nav-trust-line"
            className="hidden items-center gap-2 rounded-md border-[0.5px] border-[var(--border)] bg-[var(--surface)] px-4 py-1.5 text-xs font-semibold text-[var(--muted)] md:inline-flex"
          >
            <Flame className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" />
            {COPY.navTrustLine}
          </span>
          <button
            type="button"
            data-testid="nav-cta-button"
            onClick={scrollToWidget}
            className="shrink-0 whitespace-nowrap rounded-md bg-[var(--accent)] px-4 py-2 text-xs font-bold text-[var(--accent-ink)] transition-colors duration-200 hover:bg-[#f2c254] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)] sm:px-6 sm:py-2.5 sm:text-sm"
          >
            {COPY.finalCtaButton}
          </button>
        </div>
      </nav>

      {/* ============ ABOVE THE FOLD ============ */}
      <header className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 items-center gap-12 pb-20 pt-14 sm:pt-16 lg:grid-cols-12 lg:gap-8 lg:pt-24">
          <div className="space-y-8 lg:col-span-7">
            <div className="fade-up fade-up-1 flex flex-wrap items-center gap-3">
              <span
                data-testid="hero-live-badge"
                className="inline-flex items-center gap-2 rounded-md border-[0.5px] border-[var(--accent)] bg-transparent px-3 py-1 text-xs font-bold uppercase tracking-widest text-[var(--accent)]"
              >
                <LiveDot />
                {COPY.liveBadge}
              </span>
              <p
                data-testid="hero-eyebrow"
                className="text-sm font-semibold uppercase tracking-[0.2em] text-[var(--muted)]"
              >
                {COPY.eyebrow}
              </p>
            </div>
            <h1
              data-testid="hero-headline"
              className="fade-up fade-up-2 font-display text-[54px] font-black leading-[0.95] tracking-[-0.03em] text-[var(--text)] sm:text-8xl lg:text-[110px]"
            >
              {COPY.headlineMain}{" "}
              <span className="accent-line block w-fit">{COPY.headlineAccent}</span>
            </h1>
            <p
              data-testid="hero-subhead"
              className="fade-up fade-up-3 max-w-xl text-base leading-[1.6] text-[var(--text)] lg:text-lg"
            >
              <EmphasizedText
                text={COPY.subhead}
                highlights={COPY.subheadHighlights}
                underlines={COPY.subheadUnderlines}
              />
            </p>
            <ul data-testid="hero-bullets" className="fade-up fade-up-4 space-y-4">
              {COPY.bullets.map((bullet, i) => (
                <li
                  key={bullet}
                  data-testid={`hero-bullet-${i + 1}`}
                  className="flex items-start gap-3 text-base leading-[1.6] text-[var(--text)]"
                >
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--surface)]"
                  >
                    <Check className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={3} />
                  </span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            ref={widgetAnchorRef}
            className="scroll-mt-24 lg:col-span-5 lg:justify-self-end"
          >
            <WidgetEmbed
              flash={flash}
              showOpenButton={widgetMode !== "inline"}
              onModeChange={setWidgetMode}
            />
            {COPY.consentLine ? (
              <p
                data-testid="widget-consent-line"
                className="mx-auto mt-4 max-w-[440px] text-center text-xs leading-[1.6] text-[var(--muted)]"
              >
                {COPY.consentLine}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      {/* ============ AGENDA ============ */}
      <section data-testid="agenda-section" className="bg-[var(--bg)]">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <SectionHeading testId="agenda-heading">{COPY.agendaHeading}</SectionHeading>
          <ol className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {COPY.agenda.map((item, i) => (
              <li
                key={item}
                data-testid={`agenda-item-${i + 1}`}
                className="flex items-start gap-5 rounded-xl border-[0.5px] border-[var(--border)] bg-[var(--surface)] p-8"
              >
                <span
                  aria-hidden="true"
                  className="shrink-0 font-mono text-lg font-semibold text-[var(--accent)]"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-base leading-[1.6] text-[var(--text)]">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ PRESENTER ============ */}
      <section data-testid="presenter-section" className="bg-[var(--bg)]">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <SectionHeading testId="presenter-heading">{COPY.presenterHeading}</SectionHeading>
          <div className="mt-12 flex flex-col gap-10 sm:flex-row sm:items-start">
            {PRESENTER_IMAGE_URL ? (
              <img
                data-testid="presenter-photo"
                src={PRESENTER_IMAGE_URL}
                alt={COPY.presenterName}
                className="h-44 w-44 shrink-0 rounded-xl border-[0.5px] border-[var(--border)] object-cover"
              />
            ) : (
              <div
                data-testid="presenter-photo-placeholder"
                className="flex h-44 w-44 shrink-0 items-center justify-center rounded-xl border-[0.5px] border-[var(--border)] bg-[var(--surface)]"
              >
                <span aria-hidden="true" className="text-4xl font-bold text-[var(--muted)]">
                  HN
                </span>
              </div>
            )}
            <div className="max-w-2xl">
              <h3
                data-testid="presenter-name"
                className="text-xl font-semibold text-[var(--text)] sm:text-2xl"
              >
                {COPY.presenterName}
              </h3>
              <p
                data-testid="presenter-bio"
                className="mt-4 text-base leading-[1.6] text-[var(--muted)] lg:text-lg"
              >
                {COPY.presenterBio}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ QUALIFIER ============ */}
      <section data-testid="qualifier-section" className="bg-[var(--bg)]">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div
              data-testid="qualifier-for"
              className="rounded-xl border-[0.5px] border-[var(--border)] bg-[var(--surface)] p-8 sm:p-10"
            >
              <h3 className="text-xl font-semibold text-[var(--text)]">{COPY.forYouTitle}</h3>
              <p className="mt-4 text-base leading-[1.6] text-[var(--muted)]">{COPY.forYouBody}</p>
            </div>
            <div
              data-testid="qualifier-not-for"
              className="rounded-xl border-[0.5px] border-[var(--border)] bg-[var(--surface)] p-8 sm:p-10"
            >
              <h3 className="text-xl font-semibold text-[var(--text)]">{COPY.notForYouTitle}</h3>
              <p className="mt-4 text-base leading-[1.6] text-[var(--muted)]">{COPY.notForYouBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section data-testid="final-cta-band" className="bg-[var(--bg)]">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="flex flex-col items-start gap-8 rounded-xl border-[0.5px] border-[var(--border)] bg-[var(--surface)] p-10 sm:p-14 md:flex-row md:items-center md:justify-between">
            <p
              data-testid="final-cta-line"
              className="text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl"
            >
              {COPY.finalCtaLine}
            </p>
            <button
              type="button"
              data-testid="final-cta-button"
              onClick={scrollToWidget}
              className="shrink-0 rounded-md bg-[var(--accent)] px-8 py-4 text-base font-bold text-[var(--accent-ink)] transition-colors duration-200 hover:bg-[#f2c254] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]"
            >
              {COPY.finalCtaButton}
            </button>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer data-testid="footer" className="border-t-[0.5px] border-[var(--border)] bg-[var(--bg)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-8 pb-24 text-sm text-[var(--muted)] sm:flex-row sm:items-center sm:justify-between sm:pb-8">
          <span data-testid="footer-copyright">{COPY.footerCopyright}</span>
          <nav className="flex gap-6">
            <a
              data-testid="footer-privacy-link"
              href={PRIVACY_URL}
              className="transition-colors duration-200 hover:text-[var(--text)]"
            >
              {COPY.footerPrivacy}
            </a>
            <a
              data-testid="footer-terms-link"
              href={TERMS_URL}
              className="transition-colors duration-200 hover:text-[var(--text)]"
            >
              {COPY.footerTerms}
            </a>
          </nav>
        </div>
      </footer>
      {/* ============ STICKY MOBILE CTA ============ */}
      <div
        data-testid="sticky-mobile-cta"
        className="fixed inset-x-0 bottom-0 z-30 border-t-[0.5px] border-[var(--border)] bg-[var(--bg)]/95 p-3 backdrop-blur-sm sm:hidden"
      >
        <button
          type="button"
          data-testid="sticky-mobile-cta-button"
          onClick={scrollToWidget}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-6 py-3.5 text-sm font-bold text-[var(--accent-ink)] transition-colors duration-200 hover:bg-[#f2c254] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]"
        >
          <LiveDot dotClass="bg-[var(--accent-ink)]" />
          {COPY.finalCtaButton}
        </button>
      </div>
    </div>
  );
}
